/**
 * Geogrid Ttot Calculation Service
 * Integrates the calculateGeogridTtot function from GEOGRID_FRONTEND_UPDATED.md
 * Maps project data to calculation inputs and computes Ttot tension values
 */

import { IGeogridData, IGeogridItem } from '../api/IApiResponse';
import { GEOGRID_CONFIG } from '../config/GeogridConfig';
import { resolveGeogridSafetyFactor } from './GeogridSafetyFactor';
import { logDebug } from '../utils/utils';
import { LogTag } from '../config/loggingConfig';

// Types from GEOGRID_FRONTEND_UPDATED.md
export interface GeogridInputs {
    // LOAD SECTION
    B: number; // Deck half width (m) - C8
    L: number; // Deck half length (m) - C9
    q: number; // Uniform load (kPa) - C10

    // TRUCK LOAD FOR FIRST LIFT SECTION
    r0: number; // Radius of wheel (m) - C14 (calculated contact radius for circular)
    r0_truck?: number; // Truck wheel radius (m) - Excel B88 (physical wheel radius)
    pw: number; // Tire pressure (kPa) - C10 for contact radius calculation
    pw_firstLift?: number; // Tire pressure (kPa) - C16 for first lift membrane calculations (Excel B91)
    h_star: number; // Thickness h* (m) - C17

    // TOP COURSE SECTION
    γ1: number; // Unit weight (kN/m³) - C19
    h1: number; // Thickness (m) - C20 (tipicamente 0)
    α1: number; // Load spreading angle (deg) - C21

    // BASE COURSE SECTION
    γ2: number; // Unit weight (kN/m³) - C24
    φ2: number; // Friction angle (deg) - C25
    c2: number; // Cohesion (kPa) - C26 (tipicamente 0)
    h2: number; // Base course thickness (m) - C27
    α2: number; // Load spreading angle (deg) - C28
    Nr2: number; // Number of layers - C29 ⭐ PARAMETRO CHIAVE
    Sv2: number; // Average geogrid spacing (m) - C30
    Ka: number; // Active pressure coefficient - C31

    // SUBBASE COURSE SECTION
    γ3: number; // Unit weight (kN/m³) - C34
    φ3: number; // Friction angle (deg) - C35
    c3: number; // Cohesion (kPa) - C36 (tipicamente 0)
    h3: number; // Subbase course thickness (m) - C37
    α3: number; // Load spreading angle (deg) - C38
    Nr3: number; // Number of layers - C39
    Sv3: number; // Average geogrid spacing (m) - C40
    Ka3: number; // Active pressure coefficient - C41

    // SUBGRADE SECTION
    CBR: number; // CBR - C44
    FS: number; // FS BEARING CAPACITY - C45

    // ARRAYS (Layer data per calcoli avanzati - obbligatori)
    H: number[]; // H values per layer (B81-G81) - 6 valori
    Z: number[]; // Z coordinates (B82-G82) - 6 valori
    layerNumbers: number[]; // Layer numbers (B80-G80) - [1,2,3,4,5,6]

    // GEOMETRY AND ADDITIONAL FIELDS
    geometry?: 'Circular' | 'Rectangular' | 'Railway'; // Geometry type for calculation selection
    wheelLoad?: number; // Individual wheel load for circular geometry
    designStrainPercent?: number; // Design strain percentage (1-5%)
    courseType?: 'base' | 'subbase'; // Course type being calculated
}

export interface GeogridResult {
    value: number; // Valore specifico per il numero di layers
    column: string; // B96, C96, D96, etc.
    status: 'success' | 'error';
    message: string;
    intermediateValues?: LayerInterfaceValues; // Add intermediate calculations
    geogridDesignTable?: GeogridDesignTable; // Add complete geogrid design table
}

/**
 * Interface for VALUES AT LAYERS INTERFACES calculations
 * Matches Excel rows 59-76 (Asphalt layer bottom, Base course bottom, Subbase bottom)
 */
interface LayerInterfaceValues {
    asphaltLayerBottom: {
        z1: number; // depth (m)
        B1: number; // half width at this depth
        L1: number; // half length at this depth
        A1: number; // area (m²)
        σv1: number; // vertical stress (kPa)
    };
    baseCourseBottom: {
        z2: number; // depth (m)
        B2: number; // half width at this depth
        L2: number; // half length at this depth
        A2: number; // area (m²)
        σv2: number; // vertical stress (kPa)
    };
    subbaseBottom: {
        z3: number; // depth (m)
        B3: number; // half width at this depth
        L3: number; // half length at this depth
        A3: number; // area (m²)
        σv3: number; // vertical stress (kPa)
    };
}

/**
 * Interface for GEOGRID DESIGN FOR BASE COURSE table
 * Matches Excel rows 80-96 (complete calculation table for all layers)
 */
interface GeogridDesignTable {
    layers: GeogridLayerCalculation[];
    firstLift: {
        rw: number; // radius of wheel (m) - Excel B89
        thicknessHStar: number; // thickness h* (m) - Excel B90
        rt: number; // rt (m) - Excel B91
        tyrePressure: number; // tyre pressure (kPa) - Excel B92
        Ar: number; // Ar (m) - Excel B93
        Wtc: number; // Wtc (kPa) - Excel B94
        Tm2: number; // Tm2 (kN/m) - Excel B95
    };
    firstLiftSubbase: {
        rw: number; // radius of wheel (m) - Excel B113
        thicknessHStar: number; // thickness h* (m) - Excel B114
        rt: number; // rt (m) - Excel B115
        tyrePressure: number; // tyre pressure (kPa) - Excel B116
        Ar: number; // Ar (m) - Excel B117
        Wtc3: number; // Wtc3 (kPa) - Excel B118
        Tm3: number; // Tm3 (kN/m) - Excel B119
    };
}

/**
 * Interface for each layer calculation in the geogrid design table
 * Matches Excel columns B-G (layers 1-6) in rows 80-96
 */
export interface GeogridLayerCalculation {
    layerNumber: number; // LAYER NUMBER (Excel row 80)
    H: number; // H (m) - from base course bottom (Excel row 81)
    Z: number; // Z (m) - from top (Excel row 82)
    Tz: number; // Tz (kN/m) - Earth pressure component (Excel row 83)
    A: number; // A (m²) - Area at this layer (Excel row 84)
    σv: number; // σv (kPa) - Vertical stress (Excel row 85)
    Tp: number; // Tp (kN/m) - Geogrid tension component (Excel row 86)
    εr: number; // εr (%) - Strain (Excel row 87)
    Ω: number; // Ω - Membrane theory coefficient (Excel row 88)
    Ttot: number; // Ttot (kN/m) - Total tension (Excel row 96)
}

/**
 * Interface for tensile strength breakdown values (Tz, Tp, Tm)
 * Used for displaying the 3 contributions in online results and PDF reports
 */
export interface TensileBreakdown {
    Tz: number; // Earth pressure component (kN/m)
    Tp: number; // Geogrid tension component (kN/m)
    Tm: number; // Membrane effect - first lift only (kN/m)
    Ttot: number; // Total = Tz + Tp + Tm (kN/m)
}

/**
 * CLASSE GEOGRID CALCULATOR
 * Implementa la logica completa dal documento GEOGRID_FRONTEND_UPDATED.md
 */
export class GeogridCalculator {
    /**
     * NEW EXCEL-BASED CALCULATION: Calculate exact debug table values using Excel formulas
     * from GEOGRID DESIGN.txt - replaces deprecated calculation flows
     * Now returns the same format as calculateB96ToG96 for compatibility
     */
    calculateExcelBasedDebugTable(inputs: GeogridInputs): {
        results: number[];
        intermediateValues: LayerInterfaceValues;
        geogridDesignTable: GeogridDesignTable;
        // Legacy fields for debug table compatibility
        baseCourse: Array<any>;
        subbaseCourse: Array<any>;
        interfaceValues: LayerInterfaceValues;
    } {
        const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

        // Log input H array for debugging
        logDebug(
            'ExcelBasedDebug',
            'calculateExcelBasedDebugTable called with inputs.H array',
            {
                H_array: inputs.H,
                H_first: inputs.H[0],
                courseType: inputs.courseType,
                Nr2: inputs.Nr2,
                h2: inputs.h2,
            },
            LogTag.GEOGRID_CALC
        );

        // INTERFACE VALUES (Excel B65-B75) - "VALUES AT LAYERS INTERFACES"
        const z1 = inputs.h1; // Excel B65 = C21
        const r1 = inputs.r0 + z1 * Math.tan(toRadians(inputs.α1)); // Excel B66 = C11 + B65*TAN(RADIANS(C22))
        const σv1 = (inputs.pw * Math.pow(inputs.r0, 2)) / Math.pow(r1, 2); // Excel B67 = C10*(C11^2/B66^2)

        const z2 = inputs.h1 + inputs.h2; // Excel B69 = C21+C29
        const r2 = r1 + (z2 - z1) * Math.tan(toRadians(inputs.α2)); // Excel B70 = B66+((B69-B65)*TAN(RADIANS(C30)))
        const σv2 = (σv1 * Math.pow(r1, 2)) / Math.pow(r2, 2); // Excel B71 = B67*(B66^2/B70^2)

        const z3 = inputs.h1 + inputs.h2 + inputs.h3; // Excel B73 = C21+C29+C40
        const r3 = r2 + (z3 - z2) * Math.tan(toRadians(inputs.α3)); // Excel B74 = B70+((B73-B69)*TAN(RADIANS(C41)))
        const σv3 = (σv2 * Math.pow(r2, 2)) / Math.pow(r3, 2); // Excel B75 = B71*(B70^2/B74^2)

        // Excel constants from GEOGRID DESIGN.txt
        const K2 = Math.pow(Math.tan(((45 - inputs.φ2 / 2) * Math.PI) / 180), 2); // Excel C33 = (TAN((45-C27/2)*PI/180)^2)
        const K3 = Math.pow(Math.tan(((45 - inputs.φ3 / 2) * Math.PI) / 180), 2); // Excel C44 = (TAN((45-C38/2)*PI/180)^2)
        const H31 = (inputs.Nr2 || 1) + 1; // Excel H31 = C31+1 (number of base layers + 1)
        const H42 = (inputs.Nr3 || 1) + 1; // Excel H42 = C42+1 (number of subbase layers + 1)

        // MEMBRANE COEFFICIENTS (Excel A53-A59)
        const getMembraneCoeff = (strainPercent: number): number => {
            switch (strainPercent) {
                case 1:
                    return 2.07; // Excel B55
                case 2:
                    return 1.47; // Excel B56
                case 3:
                    return 1.23; // Excel B57
                case 4:
                    return 1.08; // Excel B58
                case 5:
                    return 0.97; // Excel B59
                default:
                    return 1.47;
            }
        };

        // BASE COURSE CALCULATIONS (Excel rows 78-100)
        const baseCourse: Array<any> = [];
        for (let layerNum = 1; layerNum <= 6; layerNum++) {
            // Excel B80-G80: H (m) - from base course bottom (user input)
            const H_value = inputs.H[layerNum - 1]; // Use actual H values from inputs array
            const H_next = layerNum < 6 ? inputs.H[layerNum] : 0; // Next layer H value

            // Excel B81-G81: Z (m) - from top
            const Z = layerNum >= H31 ? z1 : z2 - H_value; // Excel: IF(B79>=$H$31,$B$65,$B$69-B80)
            // Z_next uses (layerNum + 1) because it refers to the NEXT column (C81, D81, etc.)
            const Z_next = layerNum + 1 >= H31 ? z1 : z2 - H_next; // Excel: IF(C79>=$H$31,$B$65,$B$69-C80)

            // Excel B82-G82: Tz (kN/m) - Earth pressure component
            const Tz = layerNum >= H31 ? 0 : 0.5 * K2 * (2 * inputs.γ1 * inputs.h1 + inputs.γ2 * (Z + Z_next - 2 * inputs.h1)) * (Z - Z_next);
            // Excel: IF(B79>=$H$31,0,(0.5*$C$33*(2*$C$20*$C$21+$C$26*(B81+C81-2*$C$21))*(B81-C81)))

            // Excel B83-G83: r (m) - Radius at this depth
            const r = layerNum >= H31 ? r1 : r1 + (Z - z1) * Math.tan(toRadians(inputs.α2));
            // Excel: IF(B79>=$H$31,$B$66,$B$66+(B81-$B$65)*TAN(RADIANS($C$30)))

            // Excel B84-G84: σv (kPa) - Vertical stress
            const σv = layerNum > H31 ? 0 : (σv1 * Math.pow(r1, 2)) / Math.pow(r, 2);
            // Excel: IF(B79>$H$31,0,$B$67*$B$66^2/B83^2)

            // Calculate r_next for σv_next (next layer radius)
            const r_next = layerNum + 1 >= H31 ? r1 : r1 + (Z_next - z1) * Math.tan(toRadians(inputs.α2));
            // Excel C83: IF(C79>=$H$31,$B$66,$B$66+(C81-$B$65)*TAN(RADIANS($C$30)))

            // Excel B85-G85: Tp (kN/m) - Geogrid tension component
            // σv_next is the stress at the next layer (C84)
            const σv_next = layerNum + 1 > H31 ? 0 : (σv1 * Math.pow(r1, 2)) / Math.pow(r_next, 2);
            // Excel C84: IF(C79>$H$31,0,$B$67*$B$66^2/C83^2)
            const Tp = layerNum > H31 ? 0 : 0.5 * K2 * (σv + σv_next) * (Z - Z_next);
            // Excel: IF(B79>$H$31,0,(0.5*$C$33*(B84+C84)*(B81-C81)))

            // First lift calculations (Excel B88-B94)
            // BUG FIX: Use inputs.r0_truck and inputs.pw_firstLift for first lift tire pressure
            const r0_firstLift = inputs.r0_truck ?? 0.15; // Excel B88 = C15 (truck wheel radius)
            const h_firstLift = inputs.h_star ?? 0.3; // Excel B89 = C17 (first lift thickness)
            const rf_firstLift = r0_firstLift + h_firstLift * Math.tan(toRadians(inputs.α2)); // Excel B90
            const pw_firstLift = inputs.pw_firstLift ?? 600; // Excel B91 = C16 (tire pressure for first lift)
            const Af_firstLift = (Math.PI / 4) * Math.pow(rf_firstLift, 2); // Excel B92
            // BUG FIX: Handle undefined/null h3 as 0 for Tm2 calculation
            const Wtc2 =
                inputs.h3 && inputs.h3 > 0
                    ? 0 // Excel B93: IF($C$40>0,0,...)
                    : ((1 / 3) * inputs.γ2 * (Math.pow(rf_firstLift, 3) - Math.pow(r0_firstLift, 3))) / (Math.pow(rf_firstLift, 2) * Math.tan(toRadians(inputs.α2))) +
                      pw_firstLift * (Math.pow(r0_firstLift, 2) / Math.pow(rf_firstLift, 2)) -
                      (60 * Math.PI * inputs.CBR) / (inputs.FS || 3);
            // BUG FIX: Use design strain from inputs (2%) instead of hardcoded 5%
            const designStrainForTm2 = inputs.designStrainPercent ?? 2; // Excel B86: εr = 2%
            const Tm2 = (inputs.h3 && inputs.h3 > 0) || Wtc2 <= 0 ? 0 : Wtc2 * rf_firstLift * getMembraneCoeff(designStrainForTm2); // Excel B94

            // Excel B95-G95: Ttot (kN/m) - Total tension
            const Ttot = Tz + Tp + (layerNum === 1 ? Tm2 : 0); // Excel: B82+B85+B94

            // Debug logging for layer 1 (first geogrid)
            if (layerNum === 1) {
                logDebug(
                    'ExcelBasedDebug',
                    `BASE Layer ${layerNum} calculations`,
                    {
                        H_value: H_value.toFixed(3),
                        H_next: H_next.toFixed(3),
                        Z_calculated: Z.toFixed(3),
                        Z_next: Z_next.toFixed(3),
                        z2_baseCourseBottom: z2.toFixed(3),
                        r_radius: r.toFixed(3),
                        r_next: r_next.toFixed(3),
                        σv_stress: σv.toFixed(2),
                        σv_next: σv_next.toFixed(2),
                        Tz_earthPressure: Tz.toFixed(3),
                        Tp_geogridTension: Tp.toFixed(3),
                        Tm2_membrane: Tm2.toFixed(3),
                        Ttot_FINAL: Ttot.toFixed(3),
                        Excel_Expected: '19.85',
                    },
                    LogTag.GEOGRID_CALC
                );
            }

            baseCourse.push({
                layerNumber: layerNum,
                H: H_value, // Excel B80: H (m) - from base course bottom (user input)
                Z: Z,
                Tz: Tz,
                r: r,
                σv: σv,
                Tp: Tp,
                Ttot: Ttot,
                designStrainPercent: inputs.designStrainPercent ?? 2, // Excel B86: 2%
                Ω: layerNum === 1 ? getMembraneCoeff(inputs.designStrainPercent ?? 2) : undefined,
                firstLift:
                    layerNum === 1
                        ? {
                              r0: r0_firstLift,
                              thickness: h_firstLift,
                              rf: rf_firstLift,
                              tirePressure: pw_firstLift,
                              Af: Af_firstLift,
                              Wtc2: Wtc2,
                              Tm2: Tm2,
                          }
                        : undefined,
            });
        }

        // SUBBASE COURSE CALCULATIONS (Excel rows 102-124)
        const subbaseCourse: Array<any> = [];
        for (let layerNum = 1; layerNum <= 6; layerNum++) {
            // Excel guard: IF($C$40=0,0,...) - when h3=0, return 0 for all subbase calculations
            if (inputs.h3 === 0) {
                subbaseCourse.push({
                    layerNumber: layerNum,
                    H: 0,
                    Z: 0,
                    Tz: 0,
                    r: 0,
                    σv: 0,
                    Tp: 0,
                    Ttot: 0,
                    reason: 'Excel formula IF($C$40=0,0,...) returns 0 when subbase thickness is 0',
                });
                continue;
            }

            // Excel B104-G104: H (m) - from base course bottom (user input for subbase)
            // Note: For subbase, inputs.H contains subbase geogrid positions
            const H_value = inputs.H[layerNum - 1]; // Use actual H values from inputs array
            const H_next = layerNum < 6 ? inputs.H[layerNum] : 0; // Next layer H value

            // Excel B105-G105: Z (m) - from top
            const Z = layerNum >= H42 ? z2 : z3 - H_value; // Excel: IF($C$40=0,0,IF(B103>=$H$42,$B$69,$B$73-B104))
            // Z_next uses (layerNum + 1) because it refers to the NEXT column (C105, D105, etc.)
            const Z_next = layerNum + 1 >= H42 ? z2 : z3 - H_next; // Excel: IF($C$40=0,0,IF(C103>=$H$42,$B$69,$B$73-C104))

            // Excel B106-G106: Tz (kN/m) - Earth pressure component for SUBBASE
            // IMPORTANT: Circular geometry uses -2*z2, NOT -γ2*z1 (different from rectangular!); $C$26 is γ2, the base unit weight
            const Tz = layerNum >= H42 ? 0 : 0.5 * K3 * (2 * (inputs.γ1 * z1 + inputs.γ2 * z2 - 2 * z2) + inputs.γ3 * (Z + Z_next - 2 * z2)) * (Z - Z_next);
            // Excel: IF($C$40=0,0,IF(B103>=$H$42,0,(0.5*$C$44*(2*($C$20*$B$65+$C$26*$B$69-2*$B$69)+$C$37*(B105+C105-2*$B$69))*(B105-C105))))

            // Excel B107-G107: r (m) - Radius at this depth for SUBBASE
            const r = layerNum >= H42 ? r2 : r2 + (Z - z2) * Math.tan(toRadians(inputs.α3));
            // Excel: IF($C$40=0,0,IF(B103>=$H$42,$B$70,$B$70+(B105-$B$69)*TAN(RADIANS($C$41))))

            // Excel B108-G108: σv (kPa) - Vertical stress for SUBBASE
            const σv = layerNum > H42 ? 0 : (σv2 * Math.pow(r2, 2)) / Math.pow(r, 2);
            // Excel: IF($C$40=0,0,IF(B103>$H$42,0,$B$71*$B$70^2/B107^2))

            // Calculate r_next for σv_next (next layer radius for subbase)
            const r_next = layerNum + 1 >= H42 ? r2 : r2 + (Z_next - z2) * Math.tan(toRadians(inputs.α3));
            // Excel C107: IF($C$40=0,0,IF(C103>=$H$42,$B$70,$B$70+(C105-$B$69)*TAN(RADIANS($C$41))))

            // Excel B109-G109: Tp (kN/m) - Geogrid tension component for SUBBASE
            // σv_next is the stress at the next layer (C108)
            const σv_next = layerNum + 1 > H42 ? 0 : (σv2 * Math.pow(r2, 2)) / Math.pow(r_next, 2);
            // Excel C108: IF($C$40=0,0,IF(C103>$H$42,0,$B$71*$B$70^2/C107^2))
            const Tp = layerNum > H42 ? 0 : 0.5 * K3 * (σv + σv_next) * (Z - Z_next);
            // Excel: IF($C$40=0,0,IF(B103>$H$42,0,(0.5*$C$44*(B108+C108)*(B105-C105))))

            // First lift calculations for SUBBASE (Excel B112-B118)
            // BUG FIX: Use inputs with fallbacks (same pattern as base course)
            const r0_firstLift = inputs.r0_truck ?? 0.15; // Excel B112 = C15 (truck wheel radius)
            const h_firstLift = inputs.h_star ?? 0.3; // Excel B113 = C17 (first lift thickness)
            const rf_firstLift = r0_firstLift + h_firstLift * Math.tan(toRadians(inputs.α3)); // Excel B114
            const pw_firstLift = inputs.pw_firstLift ?? 600; // Excel B115 = C16 (tire pressure, was 500 - wrong!)
            const Af_firstLift = (Math.PI / 4) * Math.pow(rf_firstLift, 2); // Excel B116
            const Wtc3 =
                ((1 / 3) * inputs.γ3 * (Math.pow(rf_firstLift, 3) - Math.pow(r0_firstLift, 3))) / (Math.pow(rf_firstLift, 2) * Math.tan(toRadians(inputs.α3))) +
                pw_firstLift * (Math.pow(r0_firstLift, 2) / Math.pow(rf_firstLift, 2)) -
                (60 * Math.PI * inputs.CBR) / (inputs.FS || 3); // Excel B117
            // Use inputs.designStrainPercent if provided, otherwise default to 2% for subbase
            const subbaseDesignStrain = inputs.designStrainPercent ?? 2;
            const Tm3 = Wtc3 <= 0 ? 0 : Wtc3 * rf_firstLift * getMembraneCoeff(subbaseDesignStrain); // Excel B118

            // Excel B119-G119: Ttot (kN/m) - Total tension for SUBBASE
            const Ttot = Tz + Tp + (layerNum === 1 ? Tm3 : 0); // Excel: B106+B109+B118

            subbaseCourse.push({
                layerNumber: layerNum,
                H: H_value, // Excel B104: H (m) - from base course bottom (user input for subbase)
                Z: Z,
                Tz: Tz,
                r: r,
                σv: σv,
                Tp: Tp,
                Ttot: Ttot,
                // BUG FIX: Use subbaseDesignStrain variable instead of hardcoded 2
                designStrainPercent: layerNum === 1 ? subbaseDesignStrain : 1,
                Ω: layerNum === 1 ? getMembraneCoeff(subbaseDesignStrain) : undefined,
                firstLift:
                    layerNum === 1
                        ? {
                              r0: r0_firstLift,
                              thickness: h_firstLift,
                              rf: rf_firstLift,
                              tirePressure: pw_firstLift,
                              Af: Af_firstLift,
                              Wtc3: Wtc3,
                              Tm3: Tm3,
                          }
                        : undefined,
            });
        }

        // Interface values for compatibility
        const interfaceValues: LayerInterfaceValues = {
            // Asphalt layer bottom (Excel B65-B67)
            asphaltLayerBottom: {
                z1: z1,
                B1: r1,
                L1: r1,
                A1: Math.PI * Math.pow(r1, 2),
                σv1: σv1,
            },

            // Base course bottom (Excel B69-B71)
            baseCourseBottom: {
                z2: z2,
                B2: r2,
                L2: r2,
                A2: Math.PI * Math.pow(r2, 2),
                σv2: σv2,
            },

            // Subbase bottom (Excel B73-B75)
            subbaseBottom: {
                z3: z3,
                B3: r3,
                L3: r3,
                A3: Math.PI * Math.pow(r3, 2),
                σv3: σv3,
            },
        };

        // Generate results array (6 values) for main calculation compatibility
        // The main result should be the SUBBASE Layer 1 Ttot value = 4.71 kN/m
        const results: number[] = [
            subbaseCourse[0]?.Ttot ?? 0, // Layer 1: Use subbase Ttot = 4.71 kN/m
            0, // Layer 2-6: Not used in current implementation
            0,
            0,
            0,
            0,
        ];

        // Create basic geogridDesignTable for compatibility
        // Map firstLift from baseCourse[0] (BASE COURSE first lift - Excel B88-B94)
        const baseFirstLift = baseCourse[0]?.firstLift;
        const subbaseFirstLift = subbaseCourse[0]?.firstLift;

        // Combine base course and subbase course layers into unified layers array
        // Layer numbering: Sequential numbering for ALL layers (base + subbase)
        // Filter out inactive layers (those with Ttot = 0 and all components = 0)
        const activeLayers = [
            ...baseCourse
                .filter((layer, index) => index < (inputs.Nr2 || 1)) // Only include Nr2 active base layers
                .map((layer, index) => ({
                    layerNumber: index + 1,
                    H: layer.H ?? 0,
                    Z: layer.Z ?? 0,
                    Tz: layer.Tz ?? 0,
                    A: layer.A ?? 0,
                    σv: layer.σv ?? 0,
                    Tp: layer.Tp ?? 0,
                    εr: layer.εr ?? 0,
                    Ω: layer.Ω ?? 0,
                    Ttot: layer.Ttot ?? 0,
                })),
            ...subbaseCourse
                .filter((layer, index) => {
                    // Include subbase layers only if h3 > 0 and within Nr3 active layers
                    return inputs.h3 > 0 && index < (inputs.Nr3 || 1);
                })
                .map((layer, index) => ({
                    layerNumber: (inputs.Nr2 || 1) + index + 1, // Continue numbering after base course
                    H: layer.H ?? 0,
                    Z: layer.Z ?? 0,
                    Tz: layer.Tz ?? 0,
                    A: layer.A ?? 0,
                    σv: layer.σv ?? 0,
                    Tp: layer.Tp ?? 0,
                    εr: layer.εr ?? 0,
                    Ω: layer.Ω ?? 0,
                    Ttot: layer.Ttot ?? 0,
                })),
        ];

        const allLayers = activeLayers;

        const geogridDesignTable: GeogridDesignTable = {
            layers: allLayers,
            // Base course first lift (Excel B88-B94)
            firstLift: baseFirstLift
                ? {
                      rw: baseFirstLift.r0,
                      thicknessHStar: baseFirstLift.thickness,
                      rt: baseFirstLift.rf,
                      tyrePressure: baseFirstLift.tirePressure,
                      Ar: baseFirstLift.Af,
                      Wtc: baseFirstLift.Wtc2,
                      Tm2: baseFirstLift.Tm2,
                  }
                : {
                      rw: 0.15,
                      thicknessHStar: 0.3,
                      rt: 0.51,
                      tyrePressure: 500,
                      Ar: 0.2,
                      Wtc: 0,
                      Tm2: 0,
                  },
            // Subbase course first lift (Excel B112-B118)
            firstLiftSubbase: subbaseFirstLift
                ? {
                      rw: subbaseFirstLift.r0,
                      thicknessHStar: subbaseFirstLift.thickness,
                      rt: subbaseFirstLift.rf,
                      tyrePressure: subbaseFirstLift.tirePressure,
                      Ar: subbaseFirstLift.Af,
                      Wtc3: subbaseFirstLift.Wtc3,
                      Tm3: subbaseFirstLift.Tm3,
                  }
                : {
                      rw: 0.15,
                      thicknessHStar: 0.3,
                      rt: 0.51,
                      tyrePressure: 500,
                      Ar: 0.2,
                      Wtc3: 0,
                      Tm3: 0,
                  },
        };

        return {
            // New main calculation format
            results,
            intermediateValues: interfaceValues,
            geogridDesignTable,
            // Legacy debug table format (maintain backward compatibility)
            baseCourse,
            subbaseCourse,
            interfaceValues,
        };
    }
}

// Export alias for backward compatibility - to avoid breaking existing imports
export const mapProjectDataToGeogridInputs = mapGeogridDataToCalculationInputs;

/**
 * FUNZIONE PRINCIPALE - CALCOLO GEOGRID TTOT
 * Utilizza il GeogridCalculator e restituisce il valore specifico per Nr2
 */
export function calculateGeogridTtot(inputs: GeogridInputs, targetLayerIndex = 0, courseType: 'base' | 'subbase' = 'subbase'): GeogridResult {
    try {
        // === GEOMETRY ROUTING ===
        // Check if this is Rectangular or Railway geometry - if so, delegate to RectService
        const geometryType = inputs.geometry;
        if (geometryType === 'Rectangular' || geometryType === 'Railway') {
            logDebug('calculateGeogridTtot', `Routing to RectService for ${geometryType} geometry`, { geometryType, targetLayerIndex, courseType }, LogTag.GEOGRID_CALC);

            // Import RectService and map inputs
            const { calculateRectangularGeogridTtot } = require('./GeogridTtotRectService');

            // Map GeogridInputs to RectangularGeogridInputs
            const rectInputs = {
                workbook: geometryType === 'Railway' ? 'railway' : 'containerYard',
                B: inputs.B,
                L: inputs.L,
                q: inputs.q,
                A0: inputs.B * inputs.L * 4,
                r0: inputs.r0,
                pw: inputs.pw,
                h_star: inputs.h_star,
                γ1: inputs.γ1,
                h1: inputs.h1,
                α1: inputs.α1,
                γ2: inputs.γ2,
                φ2: inputs.φ2,
                c2: inputs.c2,
                h2: inputs.h2,
                α2: inputs.α2,
                Nr2: inputs.Nr2,
                Sv2: inputs.Sv2,
                K2: inputs.Ka,
                γ3: inputs.γ3,
                φ3: inputs.φ3,
                c3: inputs.c3,
                h3: inputs.h3,
                α3: inputs.α3,
                Nr3: inputs.Nr3,
                Sv3: inputs.Sv3,
                K3: inputs.Ka3,
                CBR: inputs.CBR,
                FS: inputs.FS,
                H_base: inputs.H || [0, 0, 0, 0, 0, 0],
                H_subbase: inputs.H || [0, 0, 0, 0, 0, 0],
                designStrainBase: Array.from({ length: 6 }, (_, i) => inputs.designStrainPercent || (i === 0 ? 5 : 2)),
                designStrainSubbase: Array.from({ length: 6 }, (_, i) => inputs.designStrainPercent || (i === 0 ? 5 : 2)),
            };

            const result = calculateRectangularGeogridTtot(rectInputs, targetLayerIndex, courseType);

            // Convert RectangularGeogridResult to GeogridResult
            // Map designTable to geogridDesignTable for compatibility
            // IMPORTANT: Include ALL layers (base + subbase) for test compatibility
            const geogridDesignTable = result.designTable
                ? {
                      layers: [...result.designTable.baseCourse, ...result.designTable.subbaseCourse],
                      firstLift: result.designTable.baseCourse[0]?.firstLift
                          ? {
                                rw: result.designTable.baseCourse[0].firstLift.r0,
                                thicknessHStar: result.designTable.baseCourse[0].firstLift.h_star,
                                rt: result.designTable.baseCourse[0].firstLift.rf,
                                tyrePressure: result.designTable.baseCourse[0].firstLift.pw,
                                Ar: result.designTable.baseCourse[0].firstLift.Af,
                                Wtc: result.designTable.baseCourse[0].firstLift.Wtc,
                                Tm2: result.designTable.baseCourse[0].firstLift.Tm,
                            }
                          : { rw: 0, thicknessHStar: 0, rt: 0, tyrePressure: 0, Ar: 0, Wtc: 0, Tm2: 0 },
                      firstLiftSubbase: result.designTable.subbaseCourse[0]?.firstLift
                          ? {
                                rw: result.designTable.subbaseCourse[0].firstLift.r0,
                                thicknessHStar: result.designTable.subbaseCourse[0].firstLift.h_star,
                                rt: result.designTable.subbaseCourse[0].firstLift.rf,
                                tyrePressure: result.designTable.subbaseCourse[0].firstLift.pw,
                                Ar: result.designTable.subbaseCourse[0].firstLift.Af,
                                Wtc3: result.designTable.subbaseCourse[0].firstLift.Wtc,
                                Tm3: result.designTable.subbaseCourse[0].firstLift.Tm,
                            }
                          : { rw: 0, thicknessHStar: 0, rt: 0, tyrePressure: 0, Ar: 0, Wtc3: 0, Tm3: 0 },
                  }
                : undefined;

            return {
                value: result.value,
                column: result.column,
                status: result.status,
                message: result.message,
                geogridDesignTable: geogridDesignTable as any, // Cast to match GeogridResult interface
            };
        }

        // === CIRCULAR GEOMETRY CALCULATIONS (existing logic) ===
        logDebug('calculateGeogridTtot', `Using Circular geometry calculations (geometry: ${geometryType || 'undefined'})`, { targetLayerIndex, courseType }, LogTag.GEOGRID_CALC);

        // === VALIDAZIONI INPUT ===
        if (!inputs.h2 || inputs.h2 <= 0) {
            throw new Error('h2 (Base course thickness) deve essere > 0');
        }
        if (!inputs.Nr2 || inputs.Nr2 < 1 || inputs.Nr2 > 6) {
            throw new Error(`Nr2 (Number of layers) deve essere tra 1 e 6. Received: ${inputs.Nr2}`);
        }
        if (!inputs.H || inputs.H.length !== 6) {
            throw new Error('Array H deve contenere 6 valori');
        }
        if (!inputs.Z || inputs.Z.length !== 6) {
            throw new Error('Array Z deve contenere 6 valori');
        }

        // === INSTANZIA CALCULATOR ===
        const calculator = new GeogridCalculator();

        // === CALCOLA TUTTI I VALORI USANDO EXCEL-BASED CALCULATIONS (CIRCULAR) ===
        const calculationResult = calculator.calculateExcelBasedDebugTable(inputs);
        const baseCourse = calculationResult.baseCourse;
        const subbaseCourse = calculationResult.subbaseCourse;
        const intermediateValues = calculationResult.intermediateValues;
        const geogridDesignTable = calculationResult.geogridDesignTable;

        // === SELEZIONA IL VALORE TARGET IN BASE AL COURSE TYPE ===
        // Use targetLayerIndex to select the correct layer
        // Validate that targetLayerIndex is within bounds
        // Reason: Use correct maxLayers based on courseType (Nr2 for base, Nr3 for subbase)
        const maxLayers = courseType === 'base' ? inputs.Nr2 : inputs.Nr3;
        if (targetLayerIndex < 0 || targetLayerIndex >= maxLayers) {
            targetLayerIndex = 0;
        }

        let targetValue: number;
        let columnName: string;

        // Return the value for the requested course type and layer
        if (courseType === 'base') {
            targetValue = baseCourse[targetLayerIndex]?.Ttot || 0;
            logDebug('calculateGeogridTtot', `Using BASE course value: ${targetValue}`, undefined, LogTag.GEOGRID_CALC);
        } else {
            targetValue = subbaseCourse[targetLayerIndex]?.Ttot || 0;
            logDebug('calculateGeogridTtot', `Using SUBBASE course value: ${targetValue}`, undefined, LogTag.GEOGRID_CALC);
        }
        const columns = ['B96', 'C96', 'D96', 'E96', 'F96', 'G96'];
        columnName = columns[targetLayerIndex] || 'B96';

        // Add information about which layer and total active layers
        const layerInfo = inputs.Nr2 > 1 ? ` (layer ${targetLayerIndex + 1} of ${inputs.Nr2} active layers)` : '';

        // === VALIDAZIONE RISULTATO ===
        if (targetValue === undefined || isNaN(targetValue)) {
            throw new Error(`Valore calcolato non valido per ${columnName}`);
        }

        return {
            value: parseFloat(targetValue.toFixed(4)),
            column: columnName,
            status: 'success',
            message: `${columnName} calcolato per ${inputs.Nr2} layer(s)${layerInfo}: ${targetValue.toFixed(4)} kN/m`,
            intermediateValues: intermediateValues,
            geogridDesignTable: geogridDesignTable,
        };
    } catch (error) {
        return {
            value: 0,
            column: '?96',
            status: 'error',
            message: `Errore nel calcolo: ${error instanceof Error ? error.message : 'Unknown error'}`,
        };
    }
}

// Define local interface based on the API structure
interface IBaseCourseData {
    thickness?: number;
    geogrids?: IGeogridItem[];
    unitWeight?: number;
    frictionAngle?: number;
    cohesion?: number;
    loadSpreadingAngle?: number;
}

/**
 * Genera gli array H e Z values richiesti per il calcolo
 * Basato sulla logica Excel (rows 81-82):
 * - H: sempre 0.31 per tutti i 6 layer (Excel B81-G81)
 * - Z: 0.59 per primi Nr2 layer, 0 per gli altri (Excel B82-G82)
 * @param h2 - Base course thickness
 * @param Nr2 - Number of layers
 * @param Sv2 - Average geogrid spacing (from Excel calculation)
 * @returns Object con array H e Z
 */
// Function removed - no longer used

/**
 * Genera gli array H e Z values usando la logica Excel corretta
 * AGGIORNATO: Usa le posizioni reali dei geogrids quando disponibili per custom inputs
 * @param geogrids - Array dei geogrids con posizioni reali
 * @param Nr2 - Number of active layers
 * @param h2 - Base course thickness
 * @param h1 - Top course thickness (usually 0)
 * @returns Object con array H e Z basati sulle posizioni reali dei geogrids
 */
// Function removed - no longer used

/**
 * Maps project data from projectInfo.data.geoGrid to GeogridInputs format
 * @param geoGridData - The geogrid data from project
 * @param geogridIndex - The index of the specific geogrid to calculate (0-based)
 * @param courseType - 'base' or 'subbase' to specify which course the geogrid belongs to
 * @returns GeogridInputs object ready for calculateGeogridTtot
 */
/**
 * Extracts design strain percentage from geogrid items
 * @param geogrids - Array of geogrid items
 * @returns Design strain percentage (1-5) or null if not found
 */
function extractDesignStrainFromGeogrids(geogrids: IGeogridItem[] | undefined): number | null {
    if (!geogrids || geogrids.length === 0) {
        return null;
    }

    // Find the first geogrid with a design strain; the form select gives it as text ('1'...'5')
    for (const geogrid of geogrids) {
        if (geogrid.designStrain !== undefined) {
            const strain = Number(geogrid.designStrain);
            if (strain >= 1 && strain <= 5) {
                return strain;
            }
        }
    }

    return null;
}

/**
 * Maps geogrid form data to calculation inputs for mathematical computations.
 * Used for both standalone geogrid projects and projects that come from existing projects.
 * Transforms IGeogridData format into GeogridInputs format required by calculation engines.
 *
 * @param geoGridData - Geogrid data from form (IGeogridData format)
 * @param geogridIndex - Index of the specific geogrid layer to process (default 0)
 * @param courseType - Whether processing 'base' or 'subbase' course geogrid
 * @returns GeogridInputs object ready for Ttot calculations, or null if invalid input
 */
export function mapGeogridDataToCalculationInputs(geoGridData: IGeogridData | undefined, geogridIndex = 0, courseType: 'base' | 'subbase' = 'base'): GeogridInputs | null {
    if (!geoGridData) {
        return null;
    }

    try {
        // Check if this is a circular geometry project
        const isCircularGeometry = geoGridData.type?.geogridType === 'Circular';

        // Add comprehensive debug logging for input analysis
        console.log('[GeogridCalculator:input_mapping] Starting input mapping for', {
            geogridType: geoGridData.type?.geogridType,
            isCircular: isCircularGeometry,
            courseType,
            geogridIndex,
            inputData: {
                designAxleLoad: geoGridData.load?.designAxleLoad,
                wheelLoad: geoGridData.load?.wheelLoad,
                tireInflationPressure: geoGridData.load?.tireInflationPressure,
                equivalentStandardAxleLoad: geoGridData.load?.equivalentStandardAxleLoad,
                deckHalfWidth: geoGridData.load?.deckHalfWidth,
                deckHalfLength: geoGridData.load?.deckHalfLength,
                uniformLoad: geoGridData.load?.uniformLoad,
            },
        });

        // Check if this is Railway type (no TOP COURSE in Excel)
        const isRailway = geoGridData?.type?.geogridType === 'Railway';

        // Default values - adjust based on typical project requirements
        const defaults = {
            γ1: isRailway ? 0 : (geoGridData.surfaceLayer?.unitWeight ?? 19), // kN/m³ - Railway: NO surface layer
            h1: isRailway ? 0 : (geoGridData.surfaceLayer?.thickness ?? 0), // m - Railway: NO surface layer
            α1: isRailway ? 0 : (geoGridData.surfaceLayer?.loadSpreadingAngle ?? 55), // degrees - Railway: NO surface layer, read from user input
            CBR: 0.5, // Default CBR
            c2: 0, // Cohesion typically 0 for base course
            c3: 0, // Cohesion typically 0 for subbase course
            // Note: α2 and α3 are now read from user input (loadSpreadingAngle)
        };

        // Map LOAD SECTION based on geometry type
        let B: number, L: number, q: number;

        if (isCircularGeometry) {
            // CIRCULAR GEOMETRY: Use designAxleLoad and related fields
            // Excel fields: W (designAxleLoad), p (tireInflationPressure), Pa (equivalentStandardAxleLoad)

            // For circular geometry, we don't use deckHalfWidth/Length, use calculated values
            const designAxleLoad = geoGridData.load?.designAxleLoad ?? 100; // W = 100 kN from Excel
            const tireInflationPressure = geoGridData.load?.tireInflationPressure ?? 150; // p = 150 kPa (Excel C10)
            const equivalentAxleLoad = geoGridData.load?.equivalentStandardAxleLoad ?? 80; // Pa = 80 kN

            // Calculate circular load geometry - use contact area approach
            // For circular loads, we need to calculate equivalent B, L based on contact area
            const contactRadius = Math.sqrt(designAxleLoad / (Math.PI * tireInflationPressure));
            B = contactRadius; // Half-width equivalent
            L = contactRadius; // Half-length equivalent
            q = tireInflationPressure; // Use tire pressure as uniform load

            console.log('[GeogridCalculator:circular_mapping] Circular geometry calculations:', {
                designAxleLoad: designAxleLoad,
                tireInflationPressure: tireInflationPressure,
                equivalentAxleLoad: equivalentAxleLoad,
                calculatedContactRadius: contactRadius,
                equivalentB: B,
                equivalentL: L,
                equivalentQ: q,
            });
        } else {
            // RECTANGULAR GEOMETRY: Use deckHalfWidth/Length approach
            B = geoGridData.load?.deckHalfWidth ?? 2.0;
            L = geoGridData.load?.deckHalfLength ?? 5.0;
            q = geoGridData.load?.uniformLoad ?? 80; // kPa

            console.log('[GeogridCalculator:rectangular_mapping] Rectangular geometry values:', {
                deckHalfWidth: B,
                deckHalfLength: L,
                uniformLoad: q,
            });
        }

        // Map TRUCK LOAD FOR FIRST LIFT SECTION
        // For circular geometry, calculate proper wheel load and radius
        let r0: number, pw: number;

        if (isCircularGeometry) {
            // CIRCULAR GEOMETRY: Use contact radius from designAxleLoad calculation
            const designAxleLoad = geoGridData.load?.designAxleLoad ?? 100; // W = 100 kN
            const tirePressure = geoGridData.load?.tireInflationPressure ?? 150; // p = 150 kPa (Excel C10)

            // Calculate contact radius: r = sqrt(P / (π * p)) where P is wheel load (not axle load)
            // For dual wheel axle: individual wheel load P = W/2
            const individualWheelLoad = designAxleLoad / 2; // P = W/2 = 50 kN
            r0 = Math.sqrt(individualWheelLoad / (Math.PI * tirePressure)); // Contact radius
            pw = tirePressure;

            console.log('[GeogridCalculator:wheel_calculations] Circular wheel calculations:', {
                designAxleLoad: designAxleLoad,
                individualWheelLoad: individualWheelLoad,
                tirePressure: tirePressure,
                calculatedContactRadius: r0,
                formula: 'r0 = sqrt(P / (π * p)) where P = W/2',
            });
        } else {
            // RECTANGULAR GEOMETRY: Use default values
            r0 = geoGridData.load?.wheelLoadRadius ?? geoGridData.truck?.wheelRadius ?? 0.15; // m
            pw = geoGridData.load?.tireInflationPressure ?? geoGridData.truck?.tirePressure ?? 150; // kPa (Excel C10)
        }

        const h_star = (geoGridData.load as any)?.thickness ?? geoGridData.truck?.thickness ?? 0.3; // Thickness h* (m)

        // Select the appropriate course data based on courseType
        const courseData: IBaseCourseData | undefined = courseType === 'base' ? geoGridData.geogridBaseCourse : geoGridData.geogridSubBaseCourse;

        // Get the course properties (these are separate from the geogrid course data)
        const baseCourseProps = courseType === 'base' ? geoGridData.baseCourse : geoGridData.subBaseCourse;

        // IMPORTANT: We always need BOTH base and subbase thicknesses for calculations
        // Get the actual BASE course thickness (always from baseCourse, not current courseData)
        const actualBaseThickness = geoGridData.geogridBaseCourse?.thickness ?? 0.4;
        // Get the actual SUBBASE thickness (always from subBaseCourse)
        // BUG FIX: Default to 0 (not 0.4) when no subbase exists - enables Tm2 membrane calculation
        const actualSubbaseThickness = geoGridData.geogridSubBaseCourse?.thickness ?? 0;

        if (!courseData) {
            return null;
        }

        // Debug: Log raw course data
        console.log('[GeogridCalculator:DEBUG] courseData received:', {
            courseType,
            hasGeogrids: !!courseData?.geogrids,
            geogridCount: courseData?.geogrids?.length,
            geogridsData: courseData?.geogrids,
            firstGeogridPosition: courseData?.geogrids?.[0]?.position,
            allPositions: courseData?.geogrids?.map(g => ({
                position: g.position,
                type: g.type,
                hasPosition: g.position !== undefined && g.position !== null,
            })),
        });

        // First define the other course data references
        const otherCourseData: IBaseCourseData | undefined = courseType === 'base' ? geoGridData.geogridSubBaseCourse : geoGridData.geogridBaseCourse;
        const otherCourseProps = courseType === 'base' ? geoGridData.subBaseCourse : geoGridData.baseCourse;

        // Map BASE COURSE SECTION - Always Nr2 in Excel naming
        // When courseType='base': use actual base course data
        // When courseType='subbase': use base course as "other course" for Excel formula references
        const baseData = courseType === 'base' ? courseData : otherCourseData;
        const baseCoursePropsForExcel = courseType === 'base' ? baseCourseProps : otherCourseProps;

        const γ2 = baseCoursePropsForExcel?.unitWeight ?? 19; // kN/m³ - Always BASE in Excel (γ2)
        const φ2 = baseCoursePropsForExcel?.frictionAngle ?? 30; // degrees - Always BASE in Excel (φ2)
        const c2 = baseCoursePropsForExcel?.cohesion ?? 0; // Always BASE in Excel (c2)
        const α2 = baseCoursePropsForExcel?.loadSpreadingAngle ?? 40; // degrees - Always BASE in Excel (α2, C30)
        const h2 = actualBaseThickness; // Always BASE thickness in Excel (h2)
        const Nr2 = Math.min(GEOGRID_CONFIG.MAX_LAYERS, Math.max(GEOGRID_CONFIG.MIN_LAYERS, (courseType === 'base' ? courseData.geogrids?.length : otherCourseData?.geogrids?.length) ?? 1)); // Always BASE layers in Excel (Nr2)

        // Calculate average spacing (Sv2) - Excel formula: IF(Nr2=0,0,h2/Nr2)
        const Sv2 = Nr2 > 0 ? h2 / Nr2 : 0;

        // Calculate Ka (active pressure coefficient) for BASE course
        const Ka = Math.pow(Math.tan(((45 - φ2 / 2) * Math.PI) / 180), 2);

        // Map SUBBASE COURSE SECTION - Always Nr3 in Excel naming
        // When courseType='base': use subbase course as "other course"
        // When courseType='subbase': use actual subbase course data

        const subbaseData = courseType === 'subbase' ? courseData : otherCourseData;
        const subbaseCoursePropsForExcel = courseType === 'subbase' ? baseCourseProps : otherCourseProps;

        const γ3 = subbaseCoursePropsForExcel?.unitWeight ?? 18; // kN/m³ - Always SUBBASE in Excel (γ3, C37)
        const φ3 = subbaseCoursePropsForExcel?.frictionAngle ?? 30; // degrees - Always SUBBASE in Excel (φ3)
        const c3 = subbaseCoursePropsForExcel?.cohesion ?? 0; // Always SUBBASE in Excel (c3)
        const α3 = subbaseCoursePropsForExcel?.loadSpreadingAngle ?? 40; // degrees - Always SUBBASE in Excel (α3, C41)
        const h3 = actualSubbaseThickness; // Always SUBBASE thickness in Excel (h3)
        const Nr3 = Math.min(GEOGRID_CONFIG.MAX_LAYERS, Math.max(GEOGRID_CONFIG.MIN_LAYERS, (courseType === 'subbase' ? courseData.geogrids?.length : otherCourseData?.geogrids?.length) ?? 1)); // Always SUBBASE layers in Excel (Nr3)

        // Calculate average spacing for subbase course
        let Sv3 = 0.45; // Default spacing
        const subbaseGeogridData = courseType === 'subbase' ? courseData : otherCourseData;
        if (subbaseGeogridData?.geogrids && subbaseGeogridData.geogrids.length > 1) {
            const totalSpacing = subbaseGeogridData.geogrids.reduce((sum: number, geogrid: any) => sum + (geogrid.position ?? 0), 0);
            Sv3 = totalSpacing / subbaseGeogridData.geogrids.length;
        }

        const Ka3 = Math.pow(Math.tan(((45 - φ3 / 2) * Math.PI) / 180), 2);

        // Map SUBGRADE SECTION
        const CBR = geoGridData.subgrade?.cbrSubgrade ?? defaults.CBR;
        const FS = resolveGeogridSafetyFactor(geoGridData.subgrade?.safetyFactor); // Excel C49

        // Generate layer arrays H and Z using Excel formula logic
        let H: number[], Z: number[];

        if (courseType === 'subbase') {
            // SUBBASE COURSE: Use actual geogrid positions like base course

            // Debug: Log position extraction for subbase
            const rawPositions = courseData.geogrids?.map(g => g.position);
            console.log('[GeogridCalculator:DEBUG] SUBBASE Position extraction:', {
                rawPositions,
                filteredPositions: rawPositions?.filter(p => p !== undefined),
                finalPositions: (rawPositions?.filter(p => p !== undefined) as number[]) || [],
            });

            const geogridPositions = (courseData.geogrids?.map(g => g.position).filter(p => p !== undefined) as number[]) || [];

            console.log('[GeogridCalculator:DEBUG] SUBBASE Final geogridPositions:', {
                geogridPositions,
                isEmpty: geogridPositions.length === 0,
                values: geogridPositions,
            });

            // Create H array for subbase layers
            // For subbase, H represents the position within subbase course (starts from base course bottom)
            const H_temp = new Array(6).fill(0);
            for (let i = 0; i < Math.min(geogridPositions.length, 6); i++) {
                H_temp[i] = geogridPositions[i]; // Use actual geogrid positions within subbase
            }

            // Z: Use subbase formula Excel =IF($C$40=0,0,IF(B103>=$H$42,$B$69,$B$73-B104))
            // Excel references:
            // - $C$40 = h3 (subbase thickness) = 0.4
            // - B103 = layer number (1, 2, 3, ...)
            // - $H$42 = Nr3 + 1 = 2 (Excel shows H42 = 2)
            // - $B$69 = z2 (base course bottom) = 0.4
            // - $B$73 = z3 (subbase bottom) = 0.8
            // - B104 = H value (geogrid position from base course bottom) = 0
            const z1 = defaults.h1; // Top course bottom (0)
            const z2 = defaults.h1 + actualBaseThickness; // Base course bottom (0.4)
            const z3 = defaults.h1 + actualBaseThickness + actualSubbaseThickness; // Subbase bottom (0.8)
            const H42 = Nr3 + 1; // Excel H42 = Nr3 + 1 = 2

            const Z_temp = new Array(6).fill(0);
            if (h3 > 0) {
                for (let i = 0; i < 6; i++) {
                    const layerNumber = i + 1; // Excel B103, C103, etc.
                    const H_value = H_temp[i]; // Excel B104, C104, etc.

                    // Excel formula: IF($C$40=0, 0, IF(B103>=$H$42, $B$69, $B$73-B104))
                    if (layerNumber >= H42) {
                        // Inactive layers: Z = z2 (base course bottom)
                        Z_temp[i] = z2; // 0.4
                    } else {
                        // Active layers: Z = z3 - H (subbase bottom - H value)
                        Z_temp[i] = z3 - H_value; // 0.8 - 0 = 0.8 for layer 1
                    }
                }
            } else {
                // When h3=0, all Z values are 0 (Excel formula first condition)
                Z_temp.fill(0);
            }

            H = H_temp;
            Z = Z_temp;
        } else {
            // BASE COURSE: Use actual geogrid positions instead of hardcoded Excel values

            // Debug: Log position extraction
            const rawPositions = courseData.geogrids?.map(g => g.position);
            console.log('[GeogridCalculator:DEBUG] Position extraction:', {
                rawPositions,
                filteredPositions: rawPositions?.filter(p => p !== undefined),
                finalPositions: (rawPositions?.filter(p => p !== undefined) as number[]) || [],
            });

            const geogridPositions = (courseData.geogrids?.map(g => g.position).filter(p => p !== undefined) as number[]) || [];

            console.log('[GeogridCalculator:DEBUG] Final geogridPositions:', {
                geogridPositions,
                isEmpty: geogridPositions.length === 0,
                values: geogridPositions,
            });

            const arrays = generateBaseLayerArrays(Nr2, defaults.h1, h2, geogridPositions);
            H = arrays.H;
            Z = arrays.Z;
        }

        // Debug: Log final H and Z arrays result
        console.log('[GeogridCalculator:DEBUG] Generated arrays:', {
            courseType,
            isRailway,
            H_array: H,
            Z_array: Z,
            H_firstValue: H[0],
            usesUserInput: '✅ Using user input values',
        });

        const layerNumbers = [1, 2, 3, 4, 5, 6];

        // Extract design strain percentage from geogrid data
        const designStrainPercent = extractDesignStrainFromGeogrids(courseData.geogrids) || 5; // Default 5%

        // Calculate wheelLoad for circular geometry
        const wheelLoad = isCircularGeometry ? (geoGridData.load?.designAxleLoad ?? 100) : undefined;

        const inputs: GeogridInputs = {
            B,
            L,
            q,
            r0,
            r0_truck: geoGridData.truck?.wheelRadius ?? 0.15, // Physical truck wheel radius (Excel B88)
            pw,
            pw_firstLift: geoGridData.truck?.tirePressure ?? 600, // Excel B91 = C16 (first lift tire pressure)
            h_star,
            γ1: isRailway ? 0 : (geoGridData.surfaceLayer?.unitWeight ?? defaults.γ1), // Railway: always 0 (no surface layer)
            h1: defaults.h1,
            α1: defaults.α1,
            γ2,
            φ2,
            c2,
            h2,
            α2, // Use user input instead of defaults.α2
            Nr2,
            Sv2,
            Ka,
            γ3,
            φ3,
            c3,
            h3,
            α3, // Use user input instead of defaults.α3
            Nr3,
            Sv3,
            Ka3,
            CBR,
            FS,
            H,
            Z,
            layerNumbers,
            // NEW FIELDS
            geometry: isCircularGeometry ? 'Circular' : isRailway ? 'Railway' : 'Rectangular',
            wheelLoad,
            designStrainPercent,
            courseType, // Pass the course type to calculation methods
        };

        // Final debug log showing all mapped input values
        console.log('[GeogridCalculator:final_mapped_inputs] Final mapped inputs for calculations:', {
            geometry: isCircularGeometry ? 'Circular' : 'Rectangular',
            B: inputs.B,
            L: inputs.L,
            q: inputs.q,
            r0: inputs.r0,
            pw: inputs.pw,
            γ1: inputs.γ1,
            h1: inputs.h1,
            γ2: inputs.γ2,
            φ2: inputs.φ2,
            h2: inputs.h2,
            Nr2: inputs.Nr2,
            CBR: inputs.CBR,
            FS: inputs.FS,
            H_array: inputs.H,
            Z_array: inputs.Z,
        });

        return inputs;
    } catch (error) {
        return null;
    }
}

/**
 * Calculates the required tensile strength (Ttot) for a specific geogrid
 * @param geoGridData - The geogrid data from project
 * @param geogridIndex - The index of the specific geogrid (0-based)
 * @param courseType - 'base' or 'subbase' to specify which course the geogrid belongs to
 * @returns The calculated Ttot value in kN/m, or null if calculation fails
 */
export function calculateGeogridRequiredStrength(geoGridData: IGeogridData | undefined, geogridIndex = 0, courseType: 'base' | 'subbase' = 'base'): number | null {
    logDebug('calculateGeogridRequiredStrength', `Called with courseType: ${courseType}, geogridIndex: ${geogridIndex}`, undefined, LogTag.GEOGRID_CALC);

    const inputs = mapGeogridDataToCalculationInputs(geoGridData, geogridIndex, courseType);

    if (!inputs) {
        logDebug('calculateGeogridRequiredStrength', `No inputs returned for courseType: ${courseType}`, undefined, LogTag.GEOGRID_CALC);
        return null;
    }

    // Pass geogridIndex as targetLayerIndex to get the correct layer calculation
    const result = calculateGeogridTtot(inputs, geogridIndex, courseType);

    logDebug('calculateGeogridRequiredStrength', `Result for courseType ${courseType}: ${result.status === 'success' ? result.value : 'null'}`, undefined, LogTag.GEOGRID_CALC);

    if (result.status === 'success') {
        return result.value;
    } else {
        return null;
    }
}

/**
 * UNIFIED function that automatically detects geometry type and calls the appropriate calculation service
 * This is the recommended function to use for all components (Graphic, PDF exports, etc.)
 * @param geoGridData - The geogrid data from project
 * @param geogridIndex - The index of the specific geogrid (0-based)
 * @param courseType - 'base' or 'subbase' to specify which course the geogrid belongs to
 * @returns The calculated Ttot value in kN/m, or null if calculation fails
 */
export function calculateGeogridRequiredStrengthUnified(geoGridData: IGeogridData | undefined, geogridIndex = 0, courseType: 'base' | 'subbase' = 'base'): number | null {
    if (!geoGridData) {
        return null;
    }

    const geometryType = geoGridData.type?.geogridType;

    logDebug(
        'calculateGeogridRequiredStrengthUnified',
        'Unified calculation entry point',
        {
            geometryType,
            geogridIndex,
            courseType,
        },
        LogTag.GEOGRID_CALC
    );

    // Route to appropriate calculation service based on geometry type
    // Railway uses rectangular calculations (Area = 4*B*L) with surface layer h1=0
    if (geometryType === 'Rectangular' || geometryType === 'Railway') {
        // Use rectangular calculation service for both Rectangular and Railway
        // Import dynamically to avoid circular dependencies
        const { calculateRectangularGeogridRequiredStrength } = require('./GeogridTtotRectService');
        const result = calculateRectangularGeogridRequiredStrength(geoGridData, geogridIndex, courseType);

        logDebug('calculateGeogridRequiredStrengthUnified', `Using RECTANGULAR calculation (geometryType: ${geometryType})`, { result: result?.toFixed(2) }, LogTag.GEOGRID_CALC);

        return result;
    } else {
        // Use circular calculation service (only for Circular or undefined)
        const result = calculateGeogridRequiredStrength(geoGridData, geogridIndex, courseType);

        logDebug('calculateGeogridRequiredStrengthUnified', `Using CIRCULAR calculation (geometryType: ${geometryType || 'undefined'})`, { result: result?.toFixed(2) }, LogTag.GEOGRID_CALC);

        return result;
    }
}

/**
 * Calculates the tensile strength breakdown (Tz, Tp, Tm) for a specific geogrid layer.
 * Used for displaying the 3 contributions in online results and PDF reports.
 * @param geoGridData - The geogrid data from project
 * @param geogridIndex - The index of the specific geogrid (0-based)
 * @param courseType - 'base' or 'subbase' to specify which course the geogrid belongs to
 * @returns TensileBreakdown object with Tz, Tp, Tm, Ttot values, or null if calculation fails
 */
export function calculateGeogridStrengthBreakdownUnified(geoGridData: IGeogridData | undefined, geogridIndex = 0, courseType: 'base' | 'subbase' = 'base'): TensileBreakdown | null {
    if (!geoGridData) {
        return null;
    }

    const geometryType = geoGridData.type?.geogridType;

    logDebug(
        'calculateGeogridStrengthBreakdownUnified',
        'Breakdown calculation entry point',
        {
            geometryType,
            geogridIndex,
            courseType,
        },
        LogTag.GEOGRID_CALC
    );

    // Route to appropriate calculation service based on geometry type
    if (geometryType === 'Rectangular' || geometryType === 'Railway') {
        // Use rectangular calculation service
        const { calculateRectangularGeogridStrengthBreakdown } = require('./GeogridTtotRectService');
        const result = calculateRectangularGeogridStrengthBreakdown(geoGridData, geogridIndex, courseType);

        logDebug('calculateGeogridStrengthBreakdownUnified', `Using RECTANGULAR breakdown (geometryType: ${geometryType})`, { result }, LogTag.GEOGRID_CALC);

        return result;
    } else {
        // Use circular calculation service
        const result = calculateGeogridStrengthBreakdown(geoGridData, geogridIndex, courseType);

        logDebug('calculateGeogridStrengthBreakdownUnified', `Using CIRCULAR breakdown (geometryType: ${geometryType || 'undefined'})`, { result }, LogTag.GEOGRID_CALC);

        return result;
    }
}

/**
 * Calculates the tensile strength breakdown for CIRCULAR geometry
 * @param geoGridData - The geogrid data from project
 * @param geogridIndex - The index of the specific geogrid (0-based)
 * @param courseType - 'base' or 'subbase' to specify which course the geogrid belongs to
 * @returns TensileBreakdown object with Tz, Tp, Tm, Ttot values
 */
function calculateGeogridStrengthBreakdown(geoGridData: IGeogridData | undefined, geogridIndex = 0, courseType: 'base' | 'subbase' = 'base'): TensileBreakdown | null {
    const inputs = mapGeogridDataToCalculationInputs(geoGridData, geogridIndex, courseType);

    if (!inputs) {
        return null;
    }

    try {
        const calculator = new GeogridCalculator();
        const calculationResult = calculator.calculateExcelBasedDebugTable(inputs);

        // Select the appropriate course data
        const courseData = courseType === 'base' ? calculationResult.baseCourse : calculationResult.subbaseCourse;

        // Get the layer at the specified index (geogridIndex should be 0 for first layer)
        const layerData = courseData[geogridIndex];

        if (!layerData) {
            return null;
        }

        // Extract Tz, Tp from layer data
        const Tz = layerData.Tz ?? 0;
        const Tp = layerData.Tp ?? 0;

        // Tm is only applied to layer 1 (index 0)
        let Tm = 0;
        if (geogridIndex === 0) {
            if (courseType === 'base') {
                Tm = layerData.firstLift?.Tm2 ?? 0;
            } else {
                Tm = layerData.firstLift?.Tm3 ?? 0;
            }
        }

        const Ttot = Tz + Tp + Tm;

        return {
            Tz: parseFloat(Tz.toFixed(4)),
            Tp: parseFloat(Tp.toFixed(4)),
            Tm: parseFloat(Tm.toFixed(4)),
            Ttot: parseFloat(Ttot.toFixed(4)),
        };
    } catch (error) {
        logDebug('calculateGeogridStrengthBreakdown', 'Error calculating breakdown', { error: error instanceof Error ? error.message : 'Unknown error' }, LogTag.GEOGRID_CALC);
        return null;
    }
}

/**
 * Genera gli array H e Z per BASE COURSE usando posizioni reali dei geogridi (righe 81-82)
 * H (row 81): Usa posizioni reali dei geogridi dal form
 * Z (row 82): =IF(B80>=$H$29,$B$60,$B$66-B81)
 * @param Nr2 - Number of base course layers
 * @param h1 - Top course thickness (z1 = asphalt layer bottom)
 * @param h2 - Base course thickness
 * @param geogridPositions - Array delle posizioni reali dei geogridi
 * @returns Object con array H e Z basati sulle posizioni reali e formule Excel
 */
function generateBaseLayerArrays(Nr2: number, h1 = 0, h2 = 0.9, geogridPositions: number[] = []): { H: number[]; Z: number[] } {
    // H: Usa posizioni reali dei geogridi se disponibili, altrimenti valori di default
    const H = new Array(6).fill(0);

    // Aggiorna H con le posizioni reali dei geogridi
    for (let i = 0; i < Math.min(geogridPositions.length, 6); i++) {
        H[i] = geogridPositions[i];
    }

    // Z: Applica formula Excel =IF(B80>=$H$29,$B$60,$B$66-B81)
    const z1 = h1; // B60 = asphalt layer bottom (usually 0)
    const z2 = h1 + h2; // B66 = base course bottom (h1 + h2)
    const H29 = Nr2 + 1; // H29 = Nr2 + 1

    const Z = new Array(6).fill(0);
    for (let i = 0; i < 6; i++) {
        const layerNumber = i + 1; // Layer numbers 1-6 (B80, C80, etc.)
        const H_value = H[i]; // B81, C81, etc.

        if (layerNumber >= H29) {
            // Inactive layers: Z = z1 (asphalt layer bottom)
            Z[i] = z1;
        } else {
            // Active layers: Z = z2 - H (base course bottom - H value)
            Z[i] = z2 - H_value;
        }
    }

    return { H, Z };
}

/**
 * Genera gli array H e Z per SUBBASE COURSE usando le formule Excel (righe 105-106)
 * H (row 105): Tutti valori 0.31
 * Z (row 106): =IF($C$37=0,0,IF(B104>=$H$39,$B$66,$B$72-B105))
 * @param Nr3 - Number of subbase course layers
 * @param h3 - Subbase course thickness
 * @param h1 - Top course thickness
 * @param h2 - Base course thickness
 * @returns Object con array H e Z basati sulle formule Excel
 */
// Function removed - no longer used
